// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

interface AnalysisContextType {
  isAnalyzing: boolean;
  progress: number;
  currentStage: string;
  symbol: string;
  reports: any;
  completedReports: Set<string>;
  error: string;
  startAnalysis: (symbol: string) => void;
  stopAnalysis: () => void;
  clearAnalysis: () => void;
  clearError: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

export const useAnalysis = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
};

interface AnalysisProviderProps {
  children: ReactNode;
}

export const AnalysisProvider: React.FC<AnalysisProviderProps> = ({ children }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [symbol, setSymbol] = useState('');
  const [reports, setReports] = useState<any>(null);
  const [completedReports, setCompletedReports] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const wsRef = useRef<WebSocket | null>(null);

  // Persist state to localStorage
  useEffect(() => {
    if (symbol) {
      localStorage.setItem('analysis_symbol', symbol);
    }
  }, [symbol]);

  useEffect(() => {
    if (progress > 0) {
      localStorage.setItem('analysis_progress', progress.toString());
    }
  }, [progress]);

  useEffect(() => {
    if (reports) {
      localStorage.setItem('analysis_reports', JSON.stringify(reports));
    }
  }, [reports]);

  useEffect(() => {
    if (completedReports.size > 0) {
      localStorage.setItem('analysis_completedReports', JSON.stringify([...completedReports]));
    }
  }, [completedReports]);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const savedSymbol = localStorage.getItem('analysis_symbol');
      const savedProgress = localStorage.getItem('analysis_progress');
      const savedReports = localStorage.getItem('analysis_reports');
      const savedCompleted = localStorage.getItem('analysis_completedReports');

      if (savedSymbol) setSymbol(savedSymbol);
      if (savedProgress) setProgress(parseInt(savedProgress));
      if (savedReports) setReports(JSON.parse(savedReports));
      if (savedCompleted) setCompletedReports(new Set(JSON.parse(savedCompleted)));
    } catch (err) {
      console.error('Error loading persisted analysis state:', err);
    }
  }, []);

  const startAnalysis = (newSymbol: string) => {
    if (!newSymbol.trim()) {
      setError('Please enter a symbol');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    setError('');
    setIsAnalyzing(true);
    setProgress(0);
    setReports({});
    setCompletedReports(new Set());
    setSymbol(newSymbol);
    setCurrentStage('Initializing...');

    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('You must be logged in to use AI Agent analysis');
      setIsAnalyzing(false);
      return;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const wsBase = apiBase.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsUrl = `${wsBase}/api/agent/ws/analyze/${newSymbol.toUpperCase()}?token=${token}`;

    console.log('🔗 [Global Analysis] Connecting to WebSocket:', wsUrl.replace(/token=.+/, 'token=***'));

    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      console.log('✅ [Global Analysis] WebSocket connected');
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 [Global Analysis] Message:', data.type, data.progress);

      if (data.type === 'status' || data.type === 'progress') {
        setCurrentStage(data.message || '');
        setProgress(data.progress || 0);

        if (data.report_update) {
          const reportId = data.report_update.report_id;
          const reportContent = data.report_update.content;

          console.log(`✅ [Global Analysis] Report completed: ${reportId}`);

          setReports(prev => ({
            ...prev,
            [reportId]: reportContent
          }));

          setCompletedReports(prev => new Set([...prev, reportId]));
        }
      }

      if (data.type === 'limit_exceeded') {
        const resetTime = new Date(data.reset_time);
        setError(
          `${data.message} Your limit will reset at ${resetTime.toLocaleTimeString()} on ${resetTime.toLocaleDateString()}.`
        );
        setIsAnalyzing(false);
        websocket.close();
      }

      if (data.type === 'complete') {
        console.log('🎉 [Global Analysis] Analysis complete!');
        setReports(prev => ({
          ...prev,
          ...data.reports
        }));
        setProgress(100);
        setIsAnalyzing(false);
        setCurrentStage('✅ Analysis complete!');
        websocket.close();
      }

      if (data.type === 'error') {
        setError(data.message || 'Analysis failed');
        setIsAnalyzing(false);
        websocket.close();
      }
    };

    websocket.onerror = (error) => {
      console.error('❌ [Global Analysis] WebSocket error:', error);
      setError('Connection error. Please try again.');
      setIsAnalyzing(false);
    };

    websocket.onclose = (event) => {
      console.log('🔌 [Global Analysis] WebSocket closed:', event.code);
      if (!event.wasClean && isAnalyzing) {
        setError(`Connection closed unexpectedly (code: ${event.code})`);
        setIsAnalyzing(false);
      }
    };
  };

  const stopAnalysis = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsAnalyzing(false);
  };

  const clearAnalysis = () => {
    stopAnalysis();
    setProgress(0);
    setCurrentStage('');
    setSymbol('');
    setReports(null);
    setCompletedReports(new Set());
    setError('');
    localStorage.removeItem('analysis_symbol');
    localStorage.removeItem('analysis_progress');
    localStorage.removeItem('analysis_reports');
    localStorage.removeItem('analysis_completedReports');
  };

  const clearError = () => {
    setError('');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const value = {
    isAnalyzing,
    progress,
    currentStage,
    symbol,
    reports,
    completedReports,
    error,
    startAnalysis,
    stopAnalysis,
    clearAnalysis,
    clearError
  };

  return (
    <AnalysisContext.Provider value={value}>
      {children}
    </AnalysisContext.Provider>
  );
};
